import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Envelope } from "@phosphor-icons/react";
import api, { formatApiError } from "@/lib/api";

const STATE_TEXT = {
  temp: "They'll get a fresh temporary password and the getting-started steps.",
  invite: "They'll get a fresh invitation link.",
  done: "They've already signed in, so there's nothing to resend.",
  inactive: "This account has been turned off, so there's nothing to resend.",
};

// Public page behind the "Resend now" link in the "hasn't used their new account" email.
export default function ResendWelcome() {
  const [params] = useSearchParams();
  const token = params.get("t") || "";
  const [info, setInfo] = useState(null);
  const [state, setState] = useState("ask");
  const [result, setResult] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!token) {
      setInfo({ error: "This link is missing its code. Use the link from the email." });
      return;
    }
    api.get("/public/welcome-resend", { params: { t: token } })
      .then((r) => setInfo(r.data))
      .catch((e) => setInfo({ error: formatApiError(e?.response?.data?.detail) || "This link is no longer valid." }));
  }, [token]);
  const resend = async () => {
    setState("busy");
    try {
      const { data } = await api.post("/public/welcome-resend", { token });
      setResult(data);
      setState("done");
    } catch (e) {
      setMsg(formatApiError(e?.response?.data?.detail) || "Something went wrong. Please try again.");
      setState("error");
    }
  };
  return (
    <div className="flex min-h-screen items-center justify-center bg-zinc-100 p-6" data-testid="resend-welcome-page">
      <div className="brut-card w-full max-w-md bg-white p-8">
        <Envelope size={28} weight="bold" />
        <h1 className="mt-3 font-chivo text-2xl font-black uppercase tracking-tight">Resend welcome email</h1>
        {!info && <p className="label-mono mt-3">Loading…</p>}
        {info?.error && <p className="mt-3 text-sm text-red-700" data-testid="resend-welcome-invalid">{info.error}</p>}
        {info?.email && state !== "done" && (
          <>
            <div className="mt-4 border-l-4 border-black pl-2 text-sm" data-testid="resend-welcome-person">
              <div className="font-bold">{info.name}</div>
              <div className="text-xs text-zinc-600">{info.email} · {info.role_label}</div>
            </div>
            <p className="mt-3 text-sm text-zinc-700" data-testid="resend-welcome-state">{STATE_TEXT[info.state]}</p>
            {state === "error" && <p className="mt-3 text-sm text-red-700" data-testid="resend-welcome-error">{msg}</p>}
            {!["done", "inactive"].includes(info.state) && (
              <button type="button" onClick={resend} disabled={state === "busy"} data-testid="resend-welcome-confirm"
                      className="mt-6 w-full border-2 border-black bg-black px-4 py-2.5 text-xs font-bold uppercase tracking-widest text-white transition-colors hover:bg-zinc-800 disabled:opacity-50">
                {state === "busy" ? "Sending…" : "Send it again"}
              </button>
            )}
          </>
        )}
        {state === "done" && (
          <p className="mt-3 text-sm text-zinc-700" data-testid={result.email_sent ? "resend-welcome-done" : "resend-welcome-not-sent"}>
            {result.email_sent ? `Done. A fresh welcome email is on its way to ${result.email}.`
              : "The email couldn't be sent, so nothing was changed. Sign in and use the Employees page to share their details another way."}
          </p>
        )}
        <Link to="/employees" className="mt-6 block text-xs underline" data-testid="resend-welcome-open-app">Open Employees (sign in)</Link>
      </div>
    </div>
  );
}
